import React, { useEffect, useState, useRef } from "react";
import { CPButton, CustomisedTitle, Wrapper, CustomisedTertiaryButton, TextWrapper } from "../../constants/common/StyledGlobalConstants";
import { Title } from "@vds/typography";
import { useTranslation } from "react-i18next";
import TechnicianAppointmentsModal from "./TechnicianAppointmentsModal";
import { useNavigate } from "react-router-dom";
import TechnicianAppointmentTable from "./TechnicianAppointmentScreen/TechnicianAppointmentTable";
import { useDispatch, useSelector } from "react-redux";
import { getAllAppointment } from "../../store/actions/schedulingportal/technicianAppointment";
import EmptylistNotification from "../billingPayment/EmptylistNotification";
import "./TechnicianAppointmentsModal.css";
import { chatDisplayHandler } from "../../util/chatBotUtils";
import { DEVICE_BREAKPOINTS } from "../../constants/appConstants";
import { getPendoIdPrefix } from "../../util/cfstPendoUtils";
import PropTypes from "prop-types";

const { TABLET, LARGE_DESKTOP } = DEVICE_BREAKPOINTS;

export const TechnicianAppointments = ({ serviceType, ...props }) => {
  const { t } = useTranslation("contractsTranslation") ;
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(!!serviceType);
  const [dashboardLoader, setDashboardLoader] = useState(false);
  const [isFetchJobsFailed, setIsFetchJobsFailed] = useState(false);
  const titleRef = useRef(null);
  const messageRef = useRef(null);

  useEffect(() => {
    document.title = props.title;
  }, [props.title]);

  useEffect(() => {
    serviceType === 0 && fetchJobs();
  }, [serviceType]);

  useEffect(() => {
    chatDisplayHandler(sessionStorage.getItem("currentPath"));
  }, []);

  useEffect(() => {
    if (isFetchJobsFailed) {
      if (messageRef.current) {
        messageRef.current.focus();
      }
    } else if (titleRef.current) {
      titleRef.current.focus();
    }
  }, [isFetchJobsFailed]);

  const fetchJobs = () => {
    const callBack = (loader, isApiFailed) => {
      setDashboardLoader(loader);
      setIsFetchJobsFailed(isApiFailed);
    };
    serviceType === 0 && dispatch(getAllAppointment(callBack));
  };

  const { windowSize } = useSelector((state) => state.ACCOUNT);

  const navigate = useNavigate();

  return (
    <div id="technician-appointments" aria-hidden={isOpen}>
      {isFetchJobsFailed ? (
        <EmptylistNotification title={"Something went wrong"} cfstJobFailure={isFetchJobsFailed}>
          <TextWrapper width="100%" tabIndex="-1" aria-hidden="true">
            Something went wrong while loading this page.
          </TextWrapper>
          <CustomisedTertiaryButton
            id={`${getPendoIdPrefix()}-appt-list-try-again`}
            ariaLabel="Try again"
            tabIndex="0"
            role="button"
            size="large"
            buttonWidth={"auto"}
            marginTop={"16px"}
            onClick={() => fetchJobs()}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && fetchJobs()}
            ref={messageRef}
            aria-live="assertive"
            aria-label="Something went wrong while loading this page. Try again"
          >
            TRY AGAIN
          </CustomisedTertiaryButton>
        </EmptylistNotification>
      ) : (
        <>
          <Wrapper role="region" marginBottom="24px">
            <Wrapper display="flex" flexDirection="column" gap="1rem">
              <CustomisedTitle fontSize={"40px"} lineHeight="48px">
                <Wrapper display="flex" alignItems="flex-start" flexDirection="row" justifyContent="space-between">
                  <Wrapper>
                    {!isFetchJobsFailed && (
                      <h1 className="sr-only" tabIndex="-1" ref={titleRef}>
                        {"Technician appointments"}
                      </h1>
                    )}
                    <Title tabIndex="-1" aria-hidden="true" marginTop={windowSize.width < TABLET ? "24px" : ""} size="large">
                      {t("Technician appointments")}
                    </Title>
                    <TextWrapper
                      fontStyle="normal"
                      fontWeight="400"
                      fontSize="14px"
                      lineHeight="20px"
                      marginBottom="1.5rem"
                      marginTop="24px !important"
                    >
                      Arrange for a technician to visit your location and remove or transfer a device.
                    </TextWrapper>
                  </Wrapper>
                  <Wrapper display="flex" width={windowSize.width < LARGE_DESKTOP ? "100%" : ""} flexDirection="row" gap="1rem">
                    <CPButton
                      role="button"
                      id={`${getPendoIdPrefix()}-appt-list-transfer-device`}
                      ariaLabel="Transfer A Device"
                      size="large"
                      fontSize="14px"
                      fontPaddingLeft="1rem"
                      fontPaddingRight="1rem"
                      boxButton={true}
                      buttonWidth={windowSize.width < TABLET ? "100%" : "auto"}
                      onClick={() => {
                        navigate(`/account/technicianAppointments/transferDevice`);
                      }}
                    >
                      {t("TRANSFER A DEVICE")}
                    </CPButton>
                    <CPButton
                      role="button"
                      id={`${getPendoIdPrefix()}-appt-list-remove-device`}
                      ariaLabel="Remove A Device"
                      size="large"
                      fontSize="14px"
                      fontPaddingLeft="1rem"
                      fontPaddingRight="1rem"
                      boxButton={true}
                      buttonWidth={windowSize.width < TABLET ? "100%" : "auto"}
                      marginTop={windowSize.width < TABLET ? "16px" : ""}
                      onClick={() => {
                        navigate(`/account/technicianAppointments/removeDevice`);
                      }}
                    >
                      {t("REMOVE A DEVICE")}
                    </CPButton>
                  </Wrapper>
                </Wrapper>
              </CustomisedTitle>
            </Wrapper>
          </Wrapper>
          <TechnicianAppointmentTable dashboardLoader={dashboardLoader} />
        </>
      )}
      {isOpen && <TechnicianAppointmentsModal serviceType={serviceType} isOpen={isOpen} setIsOpen={setIsOpen} />}
    </div>
  );
};

TechnicianAppointments.propTypes = {
  serviceType: PropTypes.any,
  title: PropTypes.string
};